import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  ArrowLeft,
  Loader2,
  Info,
  Check,
  Zap,
  Settings,
  User,
  FileText,
  Workflow,
  MessageSquare,
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Textarea } from "../components/ui/TextArea";
import { Card, CardContent } from "../components/ui/Card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/Select";
import { MultiSelect } from "../components/ui/MultiSelect";
import { TagManagement, TagManagementRef } from "../components/TagManagement";
import { Playbook, AccessType, UserData } from "../types";
import ToolsSelect from "../agents/ToolsSelect";
import AgentSelect from "./AgentsSelect";
import ProductSelect from "./ProductSelect";
import { useAuth } from "../contexts/AuthContext";
import { useUsersApi } from "../apis/users-apis";
import { Checkbox } from "../components/ui/Checkbox";
import { ADMIN_SERVICE_USER } from "../config/api-config";
import { useNavigate } from "react-router-dom";

interface PlaybookFormProps {
  playbook: Playbook;
  originalPlaybook?: Playbook | null;
  onPlaybookChange: (playbook: Playbook) => void;
  onSubmit: () => Promise<void>;
  onCancel: () => void;
  isEditing?: boolean;
  readOnly?: boolean;
  onEditClick?: () => void;
  onDiscardChanges?: () => void;
  hasUnsavedChanges?: boolean;
  setIsReadOnly: (value: boolean) => void;
  tagSuggestions?: string[];
  existingPlaybooks?: Playbook[];
  isSaving?: boolean;
  isInteractive?: boolean;
}

export interface PlaybookFormRef {
  validateForm: () => Promise<boolean>;
  getErrors: () => Record<string, string>;
}

// Helper functions to extract complex conditions and logic
const LoadingOverlay = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
      <Loader2 className="h-6 w-6 animate-spin text-sky-500" />
      <span className="text-lg font-medium">
        Saving Playbook... Please wait
      </span>
    </div>
  </div>
);

const getStepTitle = (currentStep: number, isInteractive: boolean): string => {
  switch (currentStep) {
    case 1:
      return "Basic Information";
    case 2:
      return "Workflow Configuration";
    case 3:
      return isInteractive ? "Tools Configuration" : "Tools & Agents";
    case 4:
      return "Additional Configuration";
    case 5:
      return "Access Control & Tags";
    default:
      return "Configuration";
  }
};

const getStepDescription = (
  currentStep: number,
  isInteractive: boolean
): string => {
  const itemType = isInteractive ? "dialogue" : "playbook";
  switch (currentStep) {
    case 1:
      return `Set up the basic details for your ${itemType} including name, description, and product.`;
    case 2:
      return isInteractive
        ? "Define the conversation flow and any special instructions for interaction."
        : "Define the workflow process and any special instructions for execution.";
    case 3:
      return isInteractive
        ? "Select the tools that will be available during conversations."
        : "Select the tools and helper agents that will be available to this playbook.";
    case 4:
      return isInteractive
        ? "Configure additional settings like model selection and chat requirements."
        : "Configure additional settings like model selection and approval requirements.";
    case 5:
      return "Set access permissions and organize with tags for easy discovery.";
    default:
      return "";
  }
};

const getHeaderTitle = (isEditing: boolean, isInteractive: boolean): string => {
  if (isEditing) {
    return `Edit ${isInteractive ? "Dialogue" : "Playbook"}`;
  }
  return `Create New ${isInteractive ? "Dialogue" : "Playbook"}`;
};

const getBackButtonText = (isInteractive: boolean): string => {
  return `Back to ${isInteractive ? "Dialogues" : "Playbooks"}`;
};

const getSubmitButtonText = (
  isEditing: boolean,
  isInteractive: boolean
): string => {
  if (isEditing) {
    return `Update ${isInteractive ? "Dialogue" : "Playbook"}`;
  }
  return `Create ${isInteractive ? "Dialogue" : "Playbook"}`;
};

const getIconGradientClass = (isInteractive: boolean): string => {
  return isInteractive
    ? "bg-gradient-to-r from-purple-500 to-violet-500"
    : "bg-gradient-to-r from-sky-500 to-cyan-500";
};

const getBackButtonClass = (isInteractive: boolean): string => {
  return isInteractive
    ? "border-purple-200 text-purple-600 hover:bg-purple-50"
    : "border-sky-200 text-sky-600 hover:bg-sky-50";
};

const createDefaultAccessControl = (): Playbook["accessControl"] => ({
  viewAccessType: "Owner Only" as AccessType,
  viewAllowedUsers: [],
  editAccessType: "Owner Only" as AccessType,
  editAllowedUsers: [],
});

const createCorePlaybookUpdate = (
  playbook: Playbook,
  adminServiceUserId: string
): Playbook => ({
  ...playbook,
  isCore: true,
  ownerId: adminServiceUserId,
  accessControl: createDefaultAccessControl(),
});

const createNonCorePlaybookUpdate = (playbook: Playbook): Playbook => ({
  ...playbook,
  isCore: false,
});

const shouldUpdateToCorePlaybook = (
  isCorePlaybook: boolean,
  adminServiceUserId: string
): boolean => {
  return isCorePlaybook && !!adminServiceUserId;
};

const shouldUpdateToNonCorePlaybook = (
  isCorePlaybook: boolean,
  playbook: Playbook
): boolean => {
  return !isCorePlaybook && playbook.isCore;
};

const shouldInitializeAccessControl = (playbook: Playbook): boolean => {
  return !playbook.accessControl && !!playbook.playbookId;
};

const clearFieldError = (
  errors: Record<string, string>,
  field: string,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
) => {
  if (errors[field]) {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }
};

// Custom hook for user management
const useUserManagement = (user: any, getUsers: () => Promise<UserData[]>) => {
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [adminServiceUserId, setAdminServiceUserId] = useState<string>("");

  const findCurrentUser = (usersData: UserData[]): UserData | undefined => {
    return usersData.find((u) => u.userId === user?.sub);
  };

  const findAdminServiceAccount = (
    usersData: UserData[]
  ): UserData | undefined => {
    return usersData.find((u) => u.email === ADMIN_SERVICE_USER);
  };

  const updateUserStates = (usersData: UserData[]) => {
    const currentUserData = findCurrentUser(usersData);
    setIsCurrentUserAdmin(currentUserData?.isAdmin || false);

    const adminServiceAccount = findAdminServiceAccount(usersData);
    if (adminServiceAccount) {
      setAdminServiceUserId(adminServiceAccount.userId);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const usersData = await getUsers();
        setUsers(usersData);
        updateUserStates(usersData);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    if (user?.sub) {
      fetchUsers();
    }
  }, [user?.sub]);

  return {
    isCurrentUserAdmin,
    users,
    isLoadingUsers,
    adminServiceUserId,
  };
};

// Custom hook for step validation
const useStepValidation = (
  playbook: Playbook,
  isInteractive: boolean,
  isEditing: boolean,
  existingPlaybooks: Playbook[] | undefined,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
) => {
  const checkNameExists = useCallback(
    (name: string): boolean => {
      if (!name || !existingPlaybooks || existingPlaybooks.length === 0)
        return false;

      return existingPlaybooks.some((existingPlaybook) => {
        const isSameName =
          existingPlaybook?.playbookName?.toLowerCase() === name.toLowerCase();

        if (isEditing && playbook?.playbookId) {
          const isDifferentPlaybook =
            existingPlaybook?.playbookId !== playbook.playbookId;
          return isSameName && isDifferentPlaybook;
        } else {
          return isSameName;
        }
      });
    },
    [existingPlaybooks, playbook?.playbookId, isEditing]
  );

  const validateStep1 = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!playbook.playbookName?.trim()) {
      newErrors.playbookName = "Playbook Name is required";
    } else if (checkNameExists(playbook.playbookName)) {
      newErrors.playbookName = "A playbook with this name already exists";
    }

    if (!playbook.playbookProductName?.trim()) {
      newErrors.playbookProductName = "Product Name is required";
    }

    if (!playbook.playbookDescription?.trim()) {
      newErrors.playbookDescription = "Playbook Description is required";
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  }, [
    playbook.playbookName,
    playbook.playbookProductName,
    playbook.playbookDescription,
    checkNameExists,
  ]);

  const validateStep2 = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!playbook.playbookWorkflow?.trim()) {
      newErrors.playbookWorkflow = "Workflow is required";
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  }, [playbook.playbookWorkflow]);

  const validateStep3 = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (isInteractive) {
      const hasTools =
        playbook.playbookActiveTools && playbook.playbookActiveTools.length > 0;
      const hasAgents =
        playbook.playbookHelperAgents &&
        playbook.playbookHelperAgents.length > 0;

      if (!hasTools && !hasAgents) {
        newErrors.playbookActiveTools =
          "At least one tool or helper agent must be selected for interactive playbooks.";
      }
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  }, [
    isInteractive,
    playbook.playbookActiveTools,
    playbook.playbookHelperAgents,
  ]);

  const validateStep4 = useCallback((): boolean => true, []);
  const validateStep5 = useCallback((): boolean => true, []);

  const validateCurrentStep = useCallback(
    (currentStep: number) => {
      switch (currentStep) {
        case 1:
          return validateStep1();
        case 2:
          return validateStep2();
        case 3:
          return validateStep3();
        case 4:
          return validateStep4();
        case 5:
          return validateStep5();
        default:
          return true;
      }
    },
    [validateStep1, validateStep2, validateStep3, validateStep4, validateStep5]
  );

  const validateAllSteps = useCallback(() => {
    const step1Valid = validateStep1();
    const step2Valid = validateStep2();
    const step3Valid = validateStep3();
    const step4Valid = validateStep4();
    const step5Valid = validateStep5();

    return step1Valid && step2Valid && step3Valid && step4Valid && step5Valid;
  }, [
    validateStep1,
    validateStep2,
    validateStep3,
    validateStep4,
    validateStep5,
  ]);

  return {
    validateStep1,
    validateStep2,
    validateStep3,
    validateStep4,
    validateStep5,
    validateCurrentStep,
    validateAllSteps,
    checkNameExists,
  };
};

// Custom hook for step navigation
const useStepNavigation = (
  totalSteps: number,
  validateCurrentStep: (step: number) => boolean
) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  const handleNextStep = () => {
    if (validateCurrentStep(currentStep)) {
      setCompletedSteps((prev) => new Set([...Array.from(prev), currentStep]));
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (step: number) => {
    setCurrentStep(step);
  };

  return {
    currentStep,
    setCurrentStep,
    completedSteps,
    setCompletedSteps,
    handleNextStep,
    handlePreviousStep,
    handleStepClick,
  };
};

// Custom hook for core playbook management
const useCorePlaybookManagement = (
  playbook: Playbook,
  onPlaybookChange: (playbook: Playbook) => void,
  userManagement: ReturnType<typeof useUserManagement>
) => {
  const [isCorePlaybook, setIsCorePlaybook] = useState(
    playbook?.isCore || false
  );

  useEffect(() => {
    if (
      shouldUpdateToCorePlaybook(
        isCorePlaybook,
        userManagement.adminServiceUserId
      )
    ) {
      const updatedPlaybook = createCorePlaybookUpdate(
        playbook,
        userManagement.adminServiceUserId
      );
      onPlaybookChange(updatedPlaybook);
    } else if (shouldUpdateToNonCorePlaybook(isCorePlaybook, playbook)) {
      const updatedPlaybook = createNonCorePlaybookUpdate(playbook);
      onPlaybookChange(updatedPlaybook);
    }
  }, [
    isCorePlaybook,
    userManagement.adminServiceUserId,
    playbook,
    onPlaybookChange,
  ]);

  const handleCorePlaybookChange = (checked: boolean) => {
    setIsCorePlaybook(checked);
  };

  return {
    isCorePlaybook,
    handleCorePlaybookChange,
  };
};

// Custom hook for access control initialization
const useAccessControlInitialization = (
  playbook: Playbook,
  onPlaybookChange: (playbook: Playbook) => void
) => {
  useEffect(() => {
    if (shouldInitializeAccessControl(playbook)) {
      onPlaybookChange({
        ...playbook,
        accessControl: createDefaultAccessControl(),
      });
    }
  }, [playbook.playbookId, playbook, onPlaybookChange]);
};

// Custom hook for input handling
const useInputHandling = (
  playbook: Playbook,
  onPlaybookChange: (playbook: Playbook) => void,
  errors: Record<string, string>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
) => {
  const handleInputChange = (field: keyof Playbook, value: any) => {
    onPlaybookChange({
      ...playbook,
      [field]: value,
    });

    clearFieldError(errors, field, setErrors);
  };

  return { handleInputChange };
};

// Step Components
const Step1BasicInfo: React.FC<{
  playbook: Playbook;
  isInteractive: boolean;
  errors: Record<string, string>;
  handleInputChange: (field: keyof Playbook, value: any) => void;
}> = ({ playbook, isInteractive, errors, handleInputChange }) => (
  <div className="space-y-6">
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {isInteractive ? "Dialogue" : "Playbook"} Name{" "}
          <span className="text-red-500">*</span>
        </label>
        <Input
          value={playbook.playbookName || ""}
          onChange={(e) => handleInputChange("playbookName", e.target.value)}
          placeholder={`Enter ${isInteractive ? "dialogue" : "playbook"} name`}
          className={errors.playbookName ? "border-red-500" : ""}
        />
        {errors.playbookName && (
          <p className="mt-1 text-sm text-red-600">{errors.playbookName}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Product Name <span className="text-red-500">*</span>
        </label>
        <ProductSelect
          selectedProduct={playbook.playbookProductName || ""}
          onChange={(value) => handleInputChange("playbookProductName", value)}
        />
        {errors.playbookProductName && (
          <p className="mt-1 text-sm text-red-600">
            {errors.playbookProductName}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description <span className="text-red-500">*</span>
        </label>
        <Textarea
          value={playbook.playbookDescription || ""}
          onChange={(e) =>
            handleInputChange("playbookDescription", e.target.value)
          }
          placeholder={`Describe what this ${isInteractive ? "dialogue" : "playbook"} does and its purpose`}
          rows={4}
          className={errors.playbookDescription ? "border-red-500" : ""}
        />
        {errors.playbookDescription && (
          <p className="mt-1 text-sm text-red-600">
            {errors.playbookDescription}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          AI Model
        </label>
        <Select
          value={playbook.playbookModel || "gpt-4o"}
          onValueChange={(value) => handleInputChange("playbookModel", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select AI model" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="gpt-4o">GPT-4o</SelectItem>
            <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
            <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
            <SelectItem value="gpt-3.5-turbo">GPT-3.5 Turbo</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  </div>
);

const Step2Workflow: React.FC<{
  playbook: Playbook;
  isInteractive: boolean;
  errors: Record<string, string>;
  handleInputChange: (field: keyof Playbook, value: any) => void;
}> = ({ playbook, isInteractive, errors, handleInputChange }) => (
  <div className="space-y-6">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Workflow <span className="text-red-500">*</span>
      </label>
      <Textarea
        value={playbook.playbookWorkflow || ""}
        onChange={(e) => handleInputChange("playbookWorkflow", e.target.value)}
        placeholder={
          isInteractive
            ? "Define the conversation flow and interaction patterns for this dialogue"
            : "Define the step-by-step workflow process for this playbook"
        }
        rows={8}
        className={errors.playbookWorkflow ? "border-red-500" : ""}
      />
      {errors.playbookWorkflow && (
        <p className="mt-1 text-sm text-red-600">{errors.playbookWorkflow}</p>
      )}
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Special Instructions
      </label>
      <Textarea
        value={playbook.playbookSpecialInstructions || ""}
        onChange={(e) =>
          handleInputChange("playbookSpecialInstructions", e.target.value)
        }
        placeholder="Any special instructions or guidelines for execution"
        rows={4}
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Approval Notes
      </label>
      <Textarea
        value={playbook.playbookApprovalNotes || ""}
        onChange={(e) =>
          handleInputChange("playbookApprovalNotes", e.target.value)
        }
        placeholder="Notes about approval requirements or processes"
        rows={3}
      />
    </div>
  </div>
);

const Step3ToolsAgents: React.FC<{
  playbook: Playbook;
  isInteractive: boolean;
  errors: Record<string, string>;
  handleInputChange: (field: keyof Playbook, value: any) => void;
}> = ({ playbook, isInteractive, errors, handleInputChange }) => (
  <div className="space-y-6">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Active Tools{isInteractive && <span className="text-red-500"> *</span>}
      </label>
      <ToolsSelect
        selectedTools={playbook.playbookActiveTools || []}
        onChange={(tools) => handleInputChange("playbookActiveTools", tools)}
      />
      {errors.playbookActiveTools && (
        <p className="mt-1 text-sm text-red-600">
          {errors.playbookActiveTools}
        </p>
      )}
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Approval Tools
      </label>
      <ToolsSelect
        selectedTools={playbook.playbookApprovalTools || []}
        onChange={(tools) => handleInputChange("playbookApprovalTools", tools)}
      />
    </div>

    {!isInteractive && (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Helper Agents
        </label>
        <AgentSelect
          selectedAgents={playbook.playbookHelperAgents || []}
          onChange={(agents) =>
            handleInputChange("playbookHelperAgents", agents)
          }
        />
      </div>
    )}
  </div>
);

// Additional step components to reduce complexity
const Step4Configuration: React.FC<{
  playbook: Playbook;
  isInteractive: boolean;
  userManagement: any;
  isCorePlaybook: boolean;
  handleInputChange: (field: keyof Playbook, value: any) => void;
  handleCorePlaybookChange: (checked: boolean) => void;
}> = ({
  playbook,
  isInteractive,
  userManagement,
  isCorePlaybook,
  handleInputChange,
  handleCorePlaybookChange,
}) => (
  <div className="space-y-6">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Chat Type
      </label>
      <Select
        value={playbook.playbookChatType || ""}
        onValueChange={(value) => handleInputChange("playbookChatType", value)}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select chat type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="standard">Standard Chat</SelectItem>
          <SelectItem value="structured">Structured Chat</SelectItem>
          <SelectItem value="workflow">Workflow Chat</SelectItem>
        </SelectContent>
      </Select>
    </div>

    {userManagement.isCurrentUserAdmin && (
      <div className="flex items-center space-x-2">
        <Checkbox
          id="core-playbook"
          checked={isCorePlaybook}
          onCheckedChange={handleCorePlaybookChange}
        />
        <label
          htmlFor="core-playbook"
          className="text-sm font-medium text-gray-700"
        >
          Mark as Core Playbook
        </label>
      </div>
    )}
  </div>
);

const Step5AccessControl: React.FC<{
  playbook: Playbook;
  isInteractive: boolean;
  userManagement: any;
  isCorePlaybook: boolean;
  tagSuggestions: string[] | undefined;
  handleInputChange: (field: keyof Playbook, value: any) => void;
  tagManagementRef: React.RefObject<TagManagementRef | null>;
}> = ({
  playbook,
  isInteractive,
  userManagement,
  isCorePlaybook,
  tagSuggestions,
  handleInputChange,
  tagManagementRef,
}) => (
  <div className="space-y-6">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Tags
      </label>
      <TagManagement
        ref={tagManagementRef}
        tags={playbook.playbookTags || []}
        onChange={(tags) => handleInputChange("playbookTags", tags)}
        suggestions={tagSuggestions}
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        View Access
      </label>
      <Select
        value={playbook.accessControl?.viewAccessType || "Owner Only"}
        onValueChange={(value) =>
          handleInputChange("accessControl", {
            ...playbook.accessControl,
            viewAccessType: value,
            viewAllowedUsers:
              value === "Owner Only"
                ? []
                : playbook.accessControl?.viewAllowedUsers || [],
          })
        }
        disabled={isCorePlaybook}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Owner Only">Owner Only</SelectItem>
          <SelectItem value="All Users">All Users</SelectItem>
          <SelectItem value="Allowed Users">Allowed Users</SelectItem>
        </SelectContent>
      </Select>
    </div>

    {playbook.accessControl?.viewAccessType === "Allowed Users" &&
      !isCorePlaybook && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Allowed Viewers
          </label>
          <MultiSelect
            options={userManagement.users.map((user: UserData) => ({
              value: user.userId,
              label: user.email,
            }))}
            value={playbook.accessControl?.viewAllowedUsers || []}
            onChange={(selectedUsers) =>
              handleInputChange("accessControl", {
                ...playbook.accessControl,
                viewAllowedUsers: selectedUsers,
              })
            }
            placeholder="Select users who can view this playbook"
          />
        </div>
      )}

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Edit Access
      </label>
      <Select
        value={playbook.accessControl?.editAccessType || "Owner Only"}
        onValueChange={(value) =>
          handleInputChange("accessControl", {
            ...playbook.accessControl,
            editAccessType: value,
            editAllowedUsers:
              value === "Owner Only"
                ? []
                : playbook.accessControl?.editAllowedUsers || [],
          })
        }
        disabled={isCorePlaybook}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Owner Only">Owner Only</SelectItem>
          <SelectItem value="All Users">All Users</SelectItem>
          <SelectItem value="Allowed Users">Allowed Users</SelectItem>
        </SelectContent>
      </Select>
    </div>

    {playbook.accessControl?.editAccessType === "Allowed Users" &&
      !isCorePlaybook && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Allowed Editors
          </label>
          <MultiSelect
            options={userManagement.users.map((user: UserData) => ({
              value: user.userId,
              label: user.email,
            }))}
            value={playbook.accessControl?.editAllowedUsers || []}
            onChange={(selectedUsers) =>
              handleInputChange("accessControl", {
                ...playbook.accessControl,
                editAllowedUsers: selectedUsers,
              })
            }
            placeholder="Select users who can edit this playbook"
          />
        </div>
      )}
  </div>
);

// Read-only view component to reduce complexity
const ReadOnlyView: React.FC<{
  playbook: Playbook;
  isInteractive: boolean;
  onEditClick?: () => void;
  onCancel: () => void;
  navigate: any;
}> = ({ playbook, isInteractive, onEditClick, onCancel, navigate }) => (
  <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-cyan-50">
    <div className="container mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8">
        <div className="mb-4 sm:mb-0">
          <div className="flex items-center gap-3 mb-2">
            <div
              className={`p-2 rounded-lg ${isInteractive ? "bg-gradient-to-r from-purple-500 to-violet-500" : "bg-gradient-to-r from-sky-500 to-cyan-500"}`}
            >
              <FileText className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              {playbook.playbookName ||
                `${isInteractive ? "Dialogue" : "Playbook"} Details`}
            </h1>
          </div>
          <p className="text-sm sm:text-base text-gray-600">
            View and manage this {isInteractive ? "dialogue" : "playbook"}{" "}
            configuration
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              navigate(`/studio/chat`, {
                replace: true,
                state: { playbookId: playbook.playbookId },
              })
            }
            className="border-green-500 text-green-500 hover:bg-green-50 hover:text-green-600"
          >
            <MessageSquare className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Chat with Agent</span>
            <span className="sm:hidden">Chat</span>
          </Button>
          {onEditClick && (
            <Button
              onClick={onEditClick}
              className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white"
              size="sm"
            >
              <Settings className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Edit Playbook</span>
              <span className="sm:hidden">Edit</span>
            </Button>
          )}
          <Button
            onClick={onCancel}
            variant="outline"
            size="sm"
            className={`${isInteractive ? "border-purple-200 text-purple-600 hover:bg-purple-50" : "border-sky-200 text-sky-600 hover:bg-sky-50"}`}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">
              Back to {isInteractive ? "Dialogues" : "Playbooks"}
            </span>
            <span className="sm:hidden">Back</span>
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
              Basic Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">
                  Description
                </label>
                <p className="text-sm sm:text-base text-gray-900 break-words">
                  {playbook.playbookDescription || "No description provided"}
                </p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">
                  Product
                </label>
                <p className="text-sm sm:text-base text-gray-900">
                  {playbook.playbookProductName || "No product specified"}
                </p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">
                  Model
                </label>
                <p className="text-sm sm:text-base text-gray-900">
                  {playbook.playbookModel || "gpt-4o"}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
              Workflow
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 font-mono text-xs sm:text-sm whitespace-pre-wrap border">
              {playbook.playbookWorkflow || "No workflow defined"}
            </div>
          </Card>

          {playbook.playbookSpecialInstructions && (
            <Card className="p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
                Special Instructions
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 text-xs sm:text-sm whitespace-pre-wrap border">
                {playbook.playbookSpecialInstructions}
              </div>
            </Card>
          )}
        </div>

        {/* Right Column - Metadata */}
        <div className="space-y-6">
          <Card className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
              Details
            </h3>
            <div className="space-y-3 text-xs sm:text-sm">
              <div>
                <span className="text-gray-500">Playbook ID:</span>
                <p className="font-mono bg-gray-50 px-2 py-1 rounded text-xs break-all">
                  {playbook.playbookId}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Chat Type:</span>
                <p className="text-gray-900">
                  {playbook.playbookChatType || "Not specified"}
                </p>
              </div>
              {playbook.isCore && (
                <div>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-800">
                    Core Playbook
                  </span>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  </div>
);

// Progress steps component to reduce complexity
const ProgressSteps: React.FC<{
  currentStep: number;
  totalSteps: number;
  completedSteps: Set<number>;
  onStepClick: (step: number) => void;
  isInteractive: boolean;
}> = ({
  currentStep,
  totalSteps,
  completedSteps,
  onStepClick,
  isInteractive,
}) => {
  const getStepLabel = (stepNum: number) => {
    switch (stepNum) {
      case 1:
        return { icon: Info, label: "Basic" };
      case 2:
        return { icon: Workflow, label: isInteractive ? "Flow" : "Workflow" };
      case 3:
        return { icon: Zap, label: isInteractive ? "Tools" : "Tools & Agents" };
      case 4:
        return { icon: Settings, label: "Config" };
      case 5:
        return { icon: User, label: "Access" };
      default:
        return { icon: Info, label: "Step" };
    }
  };

  return (
    <div className="mb-6 sm:mb-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-0">
          Step {currentStep} of {totalSteps}
        </h2>
        <div className="text-xs sm:text-sm text-gray-500">
          Progress: {Math.round((completedSteps.size / totalSteps) * 100)}%
        </div>
      </div>

      <div className="flex space-x-2 sm:space-x-4 overflow-x-auto pb-2">
        {Array.from({ length: totalSteps }, (_, i) => {
          const stepNum = i + 1;
          const isCompleted = completedSteps.has(stepNum);
          const isCurrent = stepNum === currentStep;
          const isAccessible = stepNum <= currentStep || isCompleted;
          const { icon: Icon, label } = getStepLabel(stepNum);

          return (
            <button
              key={stepNum}
              onClick={() => isAccessible && onStepClick(stepNum)}
              disabled={!isAccessible}
              className={`
                flex items-center space-x-2 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap
                ${
                  isCurrent
                    ? "bg-gradient-to-r from-sky-500 to-cyan-500 text-white shadow-lg"
                    : isCompleted
                      ? "bg-green-100 text-green-700 hover:bg-green-200"
                      : isAccessible
                        ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        : "bg-gray-50 text-gray-400 cursor-not-allowed"
                }
              `}
            >
              {isCompleted ? (
                <Check className="h-3 w-3 sm:h-4 sm:w-4" />
              ) : (
                <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
              )}
              <span className="hidden sm:inline">{label}</span>
              <span className="sm:hidden">{stepNum}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Helper functions to reduce NavigationButtons cognitive complexity
const shouldShowPreviousButton = (currentStep: number): boolean => {
  return currentStep > 1;
};

const shouldShowDiscardButton = (
  hasUnsavedChanges: boolean,
  onDiscardChanges?: () => void
): boolean => {
  return hasUnsavedChanges && !!onDiscardChanges;
};

const isLastStep = (currentStep: number, totalSteps: number): boolean => {
  return currentStep >= totalSteps;
};

const getPreviousButtonClass = (isInteractive: boolean): string => {
  return isInteractive
    ? "border-purple-200 text-purple-600 hover:bg-purple-50"
    : "border-sky-200 text-sky-600 hover:bg-sky-50";
};

const getNextButtonClass = (isInteractive: boolean): string => {
  const baseClass = "text-white";
  const interactiveClass =
    "bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600";
  const defaultClass =
    "bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600";

  return `${isInteractive ? interactiveClass : defaultClass} ${baseClass}`;
};

const getNavigationSubmitButtonText = (
  isEditing: boolean,
  isInteractive: boolean
): string => {
  const action = isEditing ? "Update" : "Create";
  const type = isInteractive ? "Dialogue" : "Playbook";
  return `${action} ${type}`;
};

const renderSubmitButtonContent = (
  isSaving: boolean,
  isEditing: boolean,
  isInteractive: boolean
) => {
  if (isSaving) {
    return (
      <>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Saving...
      </>
    );
  }

  return (
    <>
      <Check className="mr-2 h-4 w-4" />
      {getNavigationSubmitButtonText(isEditing, isInteractive)}
    </>
  );
};

// Navigation buttons component with reduced complexity
const NavigationButtons: React.FC<{
  currentStep: number;
  totalSteps: number;
  isInteractive: boolean;
  isSaving: boolean;
  isEditing: boolean;
  hasUnsavedChanges: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onSubmit: () => Promise<void>;
  onDiscardChanges?: () => void;
}> = ({
  currentStep,
  totalSteps,
  isInteractive,
  isSaving,
  isEditing,
  hasUnsavedChanges,
  onPrevious,
  onNext,
  onSubmit,
  onDiscardChanges,
}) => (
  <div className="flex flex-col sm:flex-row justify-between gap-4">
    <div className="flex gap-2">
      {shouldShowPreviousButton(currentStep) && (
        <Button
          onClick={onPrevious}
          variant="outline"
          className={getPreviousButtonClass(isInteractive)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Previous
        </Button>
      )}
    </div>

    <div className="flex gap-2">
      {shouldShowDiscardButton(hasUnsavedChanges, onDiscardChanges) && (
        <Button
          onClick={onDiscardChanges}
          variant="outline"
          className="border-gray-300 text-gray-700 hover:bg-gray-50"
          disabled={isSaving}
        >
          Discard Changes
        </Button>
      )}

      {!isLastStep(currentStep, totalSteps) ? (
        <Button onClick={onNext} className={getNextButtonClass(isInteractive)}>
          Next Step
          <ArrowLeft className="ml-2 h-4 w-4 rotate-180" />
        </Button>
      ) : (
        <Button
          onClick={onSubmit}
          className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
          disabled={isSaving}
        >
          {renderSubmitButtonContent(isSaving, isEditing, isInteractive)}
        </Button>
      )}
    </div>
  </div>
);

const PlaybookForm = forwardRef<PlaybookFormRef, PlaybookFormProps>(
  (props, ref) => {
    const navigate = useNavigate();
    const {
      playbook,
      onPlaybookChange,
      onSubmit,
      onCancel,
      isEditing = false,
      readOnly = false,
      onEditClick,
      onDiscardChanges,
      hasUnsavedChanges = false,
      tagSuggestions = [],
      existingPlaybooks,
      isSaving = false,
      isInteractive = false,
    } = props;

    // Step management
    const totalSteps = 5;
    const [errors, setErrors] = useState<Record<string, string>>({});

    // User and admin management
    const { user } = useAuth();
    const { getUsers } = useUsersApi();

    // Component refs
    const tagManagementRef = useRef<TagManagementRef>(null);

    // Core playbook state is now managed by the custom hook

    // Use custom hooks
    const userManagement = useUserManagement(user, getUsers);
    const stepValidation = useStepValidation(
      playbook,
      isInteractive,
      isEditing,
      existingPlaybooks,
      setErrors
    );
    const navigation = useStepNavigation(
      totalSteps,
      stepValidation.validateCurrentStep
    );
    const corePlaybookManagement = useCorePlaybookManagement(
      playbook,
      onPlaybookChange,
      userManagement
    );
    useAccessControlInitialization(playbook, onPlaybookChange);
    const inputHandling = useInputHandling(
      playbook,
      onPlaybookChange,
      errors,
      setErrors
    );

    const { isCorePlaybook, handleCorePlaybookChange } = corePlaybookManagement;

    const getCurrentIconGradientClass = () => {
      return getIconGradientClass(isInteractive);
    };

    const getCurrentBackButtonClass = () => {
      return getBackButtonClass(isInteractive);
    };

    useImperativeHandle(ref, () => ({
      validateForm: async (): Promise<boolean> => {
        return stepValidation.validateAllSteps();
      },
      getErrors: () => errors,
    }));

    const renderStepContent = () => {
      switch (navigation.currentStep) {
        case 1:
          return (
            <Step1BasicInfo
              playbook={playbook}
              isInteractive={isInteractive}
              errors={errors}
              handleInputChange={inputHandling.handleInputChange}
            />
          );

        case 2:
          return (
            <Step2Workflow
              playbook={playbook}
              isInteractive={isInteractive}
              errors={errors}
              handleInputChange={inputHandling.handleInputChange}
            />
          );

        case 3:
          return (
            <Step3ToolsAgents
              playbook={playbook}
              isInteractive={isInteractive}
              errors={errors}
              handleInputChange={inputHandling.handleInputChange}
            />
          );

        case 4:
          return (
            <Step4Configuration
              playbook={playbook}
              isInteractive={isInteractive}
              userManagement={userManagement}
              isCorePlaybook={isCorePlaybook}
              handleInputChange={inputHandling.handleInputChange}
              handleCorePlaybookChange={handleCorePlaybookChange}
            />
          );

        case 5:
          return (
            <Step5AccessControl
              playbook={playbook}
              isInteractive={isInteractive}
              userManagement={userManagement}
              isCorePlaybook={isCorePlaybook}
              tagSuggestions={tagSuggestions}
              handleInputChange={inputHandling.handleInputChange}
              tagManagementRef={tagManagementRef}
            />
          );

        default:
          return null;
      }
    };

    if (readOnly) {
      return (
        <ReadOnlyView
          playbook={playbook}
          isInteractive={isInteractive}
          onEditClick={onEditClick}
          onCancel={onCancel}
          navigate={navigate}
        />
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-cyan-50">
        {isSaving && <LoadingOverlay />}

        <div className="container mx-auto p-4 sm:p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8">
            <div className="mb-4 sm:mb-0">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className={`p-2 rounded-lg ${getCurrentIconGradientClass()}`}
                >
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {getHeaderTitle(isEditing, isInteractive)}
                </h1>
              </div>
              <p className="text-sm sm:text-base text-gray-600">
                {getStepDescription(navigation.currentStep, isInteractive)}
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  navigate(`/studio/chat`, {
                    replace: true,
                    state: { playbookId: playbook.playbookId },
                  })
                }
                className="border-green-500 text-green-500 hover:bg-green-50 hover:text-green-600"
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Chat with Agent</span>
                <span className="sm:hidden">Chat</span>
              </Button>
              <Button
                onClick={onCancel}
                variant="outline"
                size="sm"
                className={`${getCurrentBackButtonClass()} w-full sm:w-auto`}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">
                  {getBackButtonText(isInteractive)}
                </span>
                <span className="sm:hidden">Back</span>
              </Button>
            </div>
          </div>

          {/* Progress Steps */}
          <ProgressSteps
            currentStep={navigation.currentStep}
            totalSteps={totalSteps}
            completedSteps={navigation.completedSteps}
            onStepClick={navigation.handleStepClick}
            isInteractive={isInteractive}
          />

          {/* Main Content */}
          <Card className="mb-6">
            <CardContent className="p-4 sm:p-6">
              {renderStepContent()}
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <NavigationButtons
            currentStep={navigation.currentStep}
            totalSteps={totalSteps}
            isInteractive={isInteractive}
            isSaving={isSaving}
            isEditing={isEditing}
            hasUnsavedChanges={hasUnsavedChanges}
            onPrevious={navigation.handlePreviousStep}
            onNext={navigation.handleNextStep}
            onSubmit={onSubmit}
            onDiscardChanges={onDiscardChanges}
          />
        </div>
      </div>
    );
  }
);

export default PlaybookForm;
